/*
 * TermsPage — Public /terms
 * =========================
 * Greek Terms & Conditions (Όροι Χρήσης) for the Λευτεριά FC platform.
 */
import { Link } from "react-router-dom";
import { FileText, ShoppingCart, AlertCircle, Scale, Users, Ban } from "lucide-react";
import SEO from "../components/SEO";

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon size={22} className="text-[#F5A623]" />}
      <h2 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-white tracking-wide">{title}</h2>
    </div>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsPage = () => {
  const lastUpdated = "Φεβρουάριος 2026";
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="terms-page">
      <SEO
        title="Όροι Χρήσης · Λευτεριά FC"
        description="Όροι Χρήσης της ιστοσελίδας, εφαρμογής και υπηρεσιών της Λευτεριά FC."
      />

      <section className="pt-32 pb-16 px-4 sm:px-6 max-w-4xl mx-auto">
        <div className="mb-10">
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-2">Νομικά</p>
          <h1 className="font-['Bebas_Neue'] text-4xl md:text-5xl text-white tracking-tight mb-3">
            Όροι Χρήσης
          </h1>
          <p className="text-zinc-500 text-xs">Τελευταία ενημέρωση: {lastUpdated}</p>
        </div>

        <div className="bg-[#F5A623]/5 border border-[#F5A623]/30 rounded-lg p-4 mb-8 flex items-start gap-3">
          <Scale size={18} className="text-[#F5A623] flex-shrink-0 mt-0.5" />
          <p className="text-zinc-300 text-sm leading-relaxed">
            Οι παρόντες Όροι Χρήσης διέπουν τη χρήση της ιστοσελίδας <strong className="text-white">www.lefteriafc.cy</strong>,
            της κινητής εφαρμογής και όλων των υπηρεσιών της <strong className="text-white">Λευτεριά FC</strong>.
            Η χρήση της πλατφόρμας συνεπάγεται πλήρη αποδοχή των παρακάτω όρων.
          </p>
        </div>

        <Section icon={FileText} title="1. Ορισμοί" testid="terms-section-definitions">
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li><strong className="text-zinc-200">«Σύλλογος»</strong>: η Λευτεριά FC με έδρα τη Λεμεσό.</li>
            <li><strong className="text-zinc-200">«Πλατφόρμα»</strong>: η ιστοσελίδα, η κινητή εφαρμογή, το CMS και όλες οι σχετικές υπηρεσίες.</li>
            <li><strong className="text-zinc-200">«Χρήστης»</strong>: κάθε φυσικό/νομικό πρόσωπο που χρησιμοποιεί την Πλατφόρμα.</li>
            <li><strong className="text-zinc-200">«Μέλος»</strong>: παίκτης, γονέας, προπονητής ή στέλεχος που έχει εγγραφεί επίσημα.</li>
            <li><strong className="text-zinc-200">«KOP»</strong>: Κυπριακή Ομοσπονδία Ποδοσφαίρου.</li>
          </ul>
        </Section>

        <Section icon={Users} title="2. Λογαριασμοί & Εγγραφή" testid="terms-section-accounts">
          <p>Για να αποκτήσετε πρόσβαση σε συγκεκριμένες λειτουργίες (π.χ. εγγραφή στην ακαδημία, αγορά εισιτηρίων, PWA), απαιτείται δημιουργία λογαριασμού. Δηλώνετε ότι:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Τα στοιχεία που παρέχετε είναι αληθή, ακριβή και επικαιροποιημένα.</li>
            <li>Είστε άνω των 18 ετών ή έχετε τη συγκατάθεση γονέα/κηδεμόνα.</li>
            <li>Είστε αποκλειστικά υπεύθυνοι για την ασφάλεια του κωδικού σας.</li>
            <li>Θα ενημερώσετε άμεσα τον Σύλλογο σε περίπτωση μη εξουσιοδοτημένης χρήσης.</li>
          </ul>
          <p>Ο Σύλλογος διατηρεί το δικαίωμα να απορρίψει ή να διαγράψει λογαριασμούς που παραβιάζουν τους παρόντες Όρους.</p>
        </Section>

        <Section icon={ShoppingCart} title="3. Παραγγελίες, Πληρωμές & Ακυρώσεις" testid="terms-section-payments">
          <p><strong className="text-white">Συνδρομές Ακαδημίας:</strong></p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Καταβάλλονται μηνιαία ή περιοδικά όπως ορίζεται στη σύμβαση εγγραφής.</li>
            <li>Ακύρωση εγγραφής γίνεται με έγγραφη ειδοποίηση 30 ημερών.</li>
            <li>Δεν επιστρέφονται συνδρομές για μήνες που έχουν ήδη ξεκινήσει.</li>
          </ul>
          <p><strong className="text-white">Εισιτήρια & E-shop:</strong></p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Οι πληρωμές διεκπεραιώνονται μέσω <strong className="text-zinc-200">Stripe</strong>. Η Λευτεριά FC δεν αποθηκεύει στοιχεία κάρτας.</li>
            <li>Τα εισιτήρια αγώνων δεν επιστρέφονται εκτός αν ο αγώνας ακυρωθεί επίσημα από τη διοργανώτρια αρχή.</li>
            <li>Για φυσικά προϊόντα (merchandise) ισχύει το δικαίωμα υπαναχώρησης εντός 14 ημερολογιακών ημερών εφόσον το προϊόν επιστραφεί άθικτο.</li>
            <li>Ελαττωματικά προϊόντα αντικαθίστανται ή επιστρέφονται πλήρως εντός 30 ημερών.</li>
          </ul>
          <p><strong className="text-white">Δοκιμαστικά & Camps:</strong></p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Οι θέσεις είναι περιορισμένες και κατοχυρώνονται με σειρά κράτησης.</li>
            <li>Σε περίπτωση ακύρωσης από το Σύλλογο (π.χ. καιρικά φαινόμενα) επιστρέφεται πλήρως το ποσό ή μεταφέρεται σε επόμενη εκδήλωση.</li>
          </ul>
        </Section>

        <Section icon={AlertCircle} title="4. Υγεία, Ασφάλεια & Ιατρικές Απαιτήσεις" testid="terms-section-health">
          <p>Κάθε παίκτης οφείλει να προσκομίζει έγκυρο ιατρικό πιστοποιητικό ικανότητας άθλησης πριν από την έναρξη προπονήσεων. Η συμμετοχή σε αγώνες/προπονήσεις γίνεται με αποκλειστική ευθύνη του παίκτη ή του γονέα του για ανηλίκους. Ο Σύλλογος:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Διαθέτει εκπαιδευμένο προσωπικό πρώτων βοηθειών.</li>
            <li>Απαιτεί έγκυρη ασφαλιστική κάλυψη για κάθε παίκτη.</li>
            <li>Δεν φέρει ευθύνη για υφιστάμενες παθήσεις που δεν έχουν δηλωθεί εγγράφως.</li>
          </ul>
        </Section>

        <Section icon={Ban} title="5. Επιτρεπόμενη & Απαγορευμένη Χρήση" testid="terms-section-conduct">
          <p>Απαγορεύεται ρητά:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Η χρήση της Πλατφόρμας για παράνομες ή προσβλητικές δραστηριότητες.</li>
            <li>Η αποστολή spam, εισαγωγή malware ή προσπάθεια παραβίασης ασφάλειας.</li>
            <li>Η αντιγραφή, αναδημοσίευση ή εμπορική εκμετάλλευση περιεχομένου χωρίς άδεια.</li>
            <li>Η δημιουργία ψευδών λογαριασμών ή η υποκλοπή ταυτότητας.</li>
            <li>Η ρατσιστική, υβριστική ή διακριτική συμπεριφορά εντός ή εκτός γηπέδου.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="6. Πνευματική Ιδιοκτησία" testid="terms-section-ip">
          <p>
            Όλα τα σήματα, λογότυπα, φωτογραφίες, βίντεο, κείμενα και σχεδιαστικά στοιχεία της Πλατφόρμας ανήκουν στη Λευτεριά FC ή τους συνεργάτες της και προστατεύονται από την Κυπριακή και διεθνή νομοθεσία πνευματικής ιδιοκτησίας. Απαγορεύεται η αναπαραγωγή, τροποποίηση ή εμπορική χρήση χωρίς προηγούμενη γραπτή άδεια.
          </p>
          <p>
            Φωτογραφίες/βίντεο παικτών χρησιμοποιούνται μόνο μετά από ρητή γραπτή συγκατάθεση (δείτε <Link to="/privacy" className="text-[#F5A623] hover:underline">Πολιτική Απορρήτου</Link>).
          </p>
        </Section>

        <Section icon={Scale} title="7. Περιορισμός Ευθύνης" testid="terms-section-liability">
          <p>
            Η Λευτεριά FC καταβάλλει κάθε προσπάθεια για την ακρίβεια των πληροφοριών και τη διαθεσιμότητα της Πλατφόρμας, χωρίς όμως να παρέχει εγγυήσεις αδιάλειπτης λειτουργίας. Δεν ευθυνόμαστε για:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-zinc-400">
            <li>Έμμεσες ή επακόλουθες ζημίες από τη χρήση της Πλατφόρμας.</li>
            <li>Απώλεια δεδομένων λόγω ενέργειας του χρήστη.</li>
            <li>Περιεχόμενο τρίτων παρόχων (πληρωμές, social media, χάρτες).</li>
          </ul>
        </Section>

        <Section icon={FileText} title="8. Αλλαγές στους Όρους" testid="terms-section-changes">
          <p>
            Ο Σύλλογος δικαιούται να τροποποιεί τους παρόντες Όρους ανά πάσα στιγμή. Οι αλλαγές τίθενται σε ισχύ από τη δημοσίευσή τους στην Πλατφόρμα. Συνιστούμε την περιοδική ανάγνωση της σελίδας. Η συνέχιση χρήσης μετά από τροποποίηση συνιστά αποδοχή των νέων όρων.
          </p>
        </Section>

        <Section icon={Scale} title="9. Εφαρμοστέο Δίκαιο & Δωσιδικία" testid="terms-section-law">
          <p>
            Οι παρόντες Όροι διέπονται από το Κυπριακό Δίκαιο. Για κάθε διαφορά αρμόδια είναι τα Δικαστήρια της Λεμεσού, Κύπρου. Πριν από κάθε δικαστική διαδικασία, τα μέρη συμφωνούν να επιχειρήσουν φιλική επίλυση μέσω γραπτής επικοινωνίας εντός 30 ημερών.
          </p>
        </Section>

        <Section icon={FileText} title="10. Επικοινωνία" testid="terms-section-contact">
          <p>
            Για ερωτήσεις σχετικά με τους Όρους Χρήσης:<br />
            <strong className="text-white">Λευτεριά FC</strong> — Λεμεσός, Κύπρος<br />
            Email: <a href="mailto:info@lefteriafc.cy" className="text-[#F5A623] hover:underline">info@lefteriafc.cy</a>
          </p>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#262626] text-center">
          <Link to="/privacy" className="text-[#F5A623] hover:underline text-sm" data-testid="link-to-privacy">
            Δείτε επίσης την Πολιτική Απορρήτου →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
